/**
 * Avisos rápidos no canto da janela (toasts), como os do cliente de referência:
 * aparecem por alguns segundos e somem; erros ficam um pouco mais. Um aviso pode
 * ter uma ação ("Desfazer"), e então fica mais tempo.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { note } from '../monitoring.js';

export type ToastKind = 'info' | 'success' | 'error';
export type ToastAction = { label: string; run: () => void };
type Toast = { id: number; text: string; kind: ToastKind; action?: ToastAction };

type ToastState = {
  toasts: Toast[];
  push: (text: string, kind?: ToastKind, action?: ToastAction) => void;
  dismiss: (id: number) => void;
};

let nextId = 1;

export const useToasts = create<ToastState>()((set) => ({
  toasts: [],
  push: (text, kind = 'info', action) =>
    set((s) => {
      // o mesmo aviso repetido não empilha (o de ação troca o anterior: o "Desfazer" vale para o último)
      const others = action ? s.toasts.filter((toast) => toast.text !== text) : s.toasts;
      if (!action && others.some((toast) => toast.text === text)) return s;
      return { toasts: [...others, { id: nextId++, text, kind, ...(action ? { action } : {}) }].slice(-4) };
    }),
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((toast) => toast.id !== id) })),
}));

export function toast(text: string, kind: ToastKind = 'info', action?: ToastAction): void {
  // o que a pessoa viu na tela entra no caminho até um erro no Sentry
  note('toast', text, kind === 'error' ? 'error' : 'info');
  useToasts.getState().push(text, kind, action);
}

export function Toaster() {
  const toasts = useToasts((s) => s.toasts);
  return (
    <div className="rd-toaster" role="status" aria-live="polite">
      {toasts.map((item) => (
        <ToastView key={item.id} item={item} />
      ))}
    </div>
  );
}

function ToastView({ item }: { item: Toast }) {
  const dismiss = useToasts((s) => s.dismiss);
  useEffect(() => {
    const timer = window.setTimeout(() => dismiss(item.id), item.kind === 'error' || item.action ? 6000 : 3500);
    return () => window.clearTimeout(timer);
  }, [item.id, item.kind, item.action, dismiss]);
  if (item.action) {
    const action = item.action;
    return (
      <div className={`rd-toast rd-toast--${item.kind} rd-toast--action`}>
        <span>{item.text}</span>
        <button
          type="button"
          className="rd-toast__action"
          onClick={() => {
            dismiss(item.id);
            action.run();
          }}
        >
          {action.label}
        </button>
      </div>
    );
  }
  return (
    <button type="button" className={`rd-toast rd-toast--${item.kind}`} onClick={() => dismiss(item.id)}>
      {item.text}
    </button>
  );
}
